import { persistentAtom } from '@nanostores/persistent';
import { computed, map } from 'nanostores';

import { clamp } from '@iconik-io/core/src/clamp';
import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { Timecode, type TimecodeInitValue } from '@iconik-io/core/src/timecode';
import type { RequiredBy } from '@iconik-io/core/src/types';

import type { SyncClient } from '../../sync/client/sync_client.js';
import { syncAtom } from '../../sync/sync_atom.js';
import type { NewPlaylistItem, PlaylistItem } from '../../types.js';
import { getFrameAlignedTime } from '../../utils/get_frame_aligned_time.js';
import { getPlaylistItemWithDefaults } from '../../utils/get_playlist_item_with_defaults.js';
import { booleanEncoder } from './persistent_encoding.js';

const logger = DebugLogger.getInstance('unified_player_playlist');

export interface Playlist<PlaylistItemMeta> {
  [key: string]: PlaylistItem<PlaylistItemMeta>;
}

function alignPlaylist<PlaylistItemMeta>(playlist: Playlist<PlaylistItemMeta>) {
  const playlistCopy = { ...playlist };
  for (const item of Object.values(playlistCopy)) {
    alignPlayableRangeTimesToFrame(item);
  }
  return playlistCopy;
}

function alignPlayableRangeTimesToFrame<PlaylistItemMeta>(
  item: PlaylistItem<PlaylistItemMeta>,
) {
  if (!item.playableRange) {
    return item;
  }
  const { end, start } = item.playableRange;
  const alignedRange = {
    start: getFrameAlignedTime(start, item),
    end: getFrameAlignedTime(end, item),
  };
  if (alignedRange.start !== start || alignedRange.end !== end) {
    return { ...item, playableRange: alignedRange };
  }
  return item;
}

export function createPlaylistStore<PlaylistItemMeta>(syncClient: SyncClient) {
  const $unorderedPlaylist = map<Playlist<PlaylistItemMeta>>({});

  const $playlistOrder = map<Record<string, number>>({});

  const $playlist = computed(
    [$unorderedPlaylist, $playlistOrder],
    (unorderedPlaylist, order): Playlist<PlaylistItemMeta> => {
      const orderedPlaylist: Playlist<PlaylistItemMeta> = {};
      // Sort by order value
      const sortedIds = Object.entries(order)
        .sort(([, orderA], [, orderB]) => orderA - orderB)
        .map(([id]) => id);

      // add items in order
      for (const id of sortedIds) {
        if (unorderedPlaylist[id]) {
          orderedPlaylist[id] = unorderedPlaylist[id];
        }
      }
      return orderedPlaylist;
    },
  );

  const $currentPlaylistItemId = syncAtom<string | undefined>(
    'currentPlaylistItemId',
    undefined,
    {
      syncClient,
    },
  );

  const $currentVersionId = syncAtom<string | undefined>(
    'currentVersionId',
    undefined,
    {
      syncClient,
    },
  );

  const $currentPlaylistItem = computed(
    [$playlist, $currentPlaylistItemId],
    (playlist, itemId): PlaylistItem<PlaylistItemMeta> | undefined =>
      itemId ? playlist[itemId] : undefined,
  );

  const $readTimecodeOffsetFromSource = persistentAtom<boolean>(
    'UP_readTimecodeOffsetFromSource',
    true,
    booleanEncoder,
  );

  function toggleReadTimecodeOffsetFromSource() {
    $readTimecodeOffsetFromSource.set(!$readTimecodeOffsetFromSource.get());
  }

  // First frame offset is the number of frames when timecode of the
  // video starts.
  const $firstFrameOffset = computed(
    [$currentPlaylistItem, $readTimecodeOffsetFromSource],
    (currentItem, readTimecodeOffsetFromSource) => {
      if (!currentItem?.firstFrameTimecode || !readTimecodeOffsetFromSource) {
        return 0;
      }
      const firstFrameTimecode = createTimecode({
        smpte: currentItem.firstFrameTimecode,
      });

      return firstFrameTimecode.frames;
    },
  );

  function setPlaylist(playlist: Playlist<PlaylistItemMeta>) {
    const alignedPlaylist = alignPlaylist(playlist);
    $unorderedPlaylist.set(alignedPlaylist);

    const orderMap = Object.fromEntries(
      Object.keys(alignedPlaylist).map((id, index) => [id, index + 1]),
    );
    $playlistOrder.set(orderMap);
  }

  function addPlaylistItem(item: NewPlaylistItem<PlaylistItemMeta>) {
    const playlistItem = getPlaylistItemWithDefaults(item);

    $unorderedPlaylist.setKey(
      playlistItem.id,
      alignPlayableRangeTimesToFrame(playlistItem),
    );

    // Get the highest existing order value and add 1
    const currentOrders = Object.values($playlistOrder.get());
    const nextOrder =
      currentOrders.length > 0 ? Math.max(...currentOrders) + 1 : 0;

    $playlistOrder.setKey(playlistItem.id, nextOrder);
  }

  function setCurrentPlaylistItemId(itemId: string | undefined) {
    $currentPlaylistItemId.set(itemId);
  }

  function setCurrentVersionId(versionId: string | undefined) {
    $currentVersionId.set(versionId);
  }

  function removeFromPlaylist(key: string) {
    const currentPlaylistItemId = $currentPlaylistItem.get()?.id;

    if (currentPlaylistItemId === key) {
      const playlistIds = Object.keys($playlist.get());
      const currentKeyIndex = playlistIds.indexOf(key);
      const nextId =
        playlistIds[currentKeyIndex] ?? playlistIds[currentKeyIndex - 1];
      setCurrentPlaylistItemId(nextId);
    }
    $unorderedPlaylist.setKey(key, undefined);
    $playlistOrder.setKey(key, undefined);
  }

  function updatePlaylistItem(
    item: RequiredBy<Partial<PlaylistItem<PlaylistItemMeta>>, 'id'>,
  ) {
    logger.debug('Updating playlist item', item);
    const playlistItem = $unorderedPlaylist.get()[item.id];
    $unorderedPlaylist.setKey(
      playlistItem.id,
      alignPlayableRangeTimesToFrame({ ...playlistItem, ...item }),
    );
  }

  function updateCurrentPlaylistItem(
    item: Partial<Omit<PlaylistItem<PlaylistItemMeta>, 'id'>>,
  ) {
    const playlistItemId = $currentPlaylistItemId.get();
    if (!playlistItemId) {
      return;
    }
    updatePlaylistItem({ id: playlistItemId, ...item });
  }

  function jumpToNext() {
    const currentItemId = $currentPlaylistItemId.get();
    if (!currentItemId) {
      return;
    }
    const playlistIds = Object.keys($playlist.get());
    const currentIndex = playlistIds.indexOf(currentItemId);
    if (currentIndex + 1 < playlistIds.length) {
      setCurrentPlaylistItemId(playlistIds[currentIndex + 1]);
    }
  }

  function jumpToPrevious() {
    const currentItemId = $currentPlaylistItemId.get();
    if (!currentItemId) {
      return;
    }
    const playlistIds = Object.keys($playlist.get());
    const currentIndex = playlistIds.indexOf(currentItemId);
    if (currentIndex > 0) {
      setCurrentPlaylistItemId(playlistIds[currentIndex - 1]);
    }
  }

  function setCurrentPlaylistIndex(index: number) {
    const playlistIds = Object.keys($playlist.get());
    const effectiveIndex = clamp(index, 0, playlistIds.length - 1);
    setCurrentPlaylistItemId(playlistIds[effectiveIndex]);
  }

  function createTimecode(value: TimecodeInitValue): Timecode {
    const currentItem = $currentPlaylistItem.get();
    const newTimecode = new Timecode(
      {
        frameRate: currentItem?.frameRate ?? 25,
        isDropFrame: currentItem?.isDropFrame,
        isMillisecondsMode: currentItem?.contentType === 'audio',
      },
      value,
    );
    return newTimecode;
  }

  const $frameDuration = computed(
    [$currentPlaylistItem],
    (currentPlaylistItem): number => {
      if (currentPlaylistItem?.contentType === 'pdf') {
        return 1 / 1000;
      }
      const frameRate = currentPlaylistItem?.frameRate;
      if (!frameRate) {
        return 0;
      }
      return 1 / frameRate;
    },
  );

  function setPlaylistItemOrder(
    itemId: string,
    options: { beforeId?: string; afterId?: string },
  ) {
    const { beforeId, afterId } = options;
    const currentOrders = $playlistOrder.get();

    // Get the order's we'll be placing between
    // No start value means we're adding to the end
    // No end value means we're adding to the start
    const startValue = afterId ? currentOrders[afterId] : 0;
    const endValue = beforeId ? currentOrders[beforeId] : startValue + 1;

    // Calculate new order value as average
    const newOrder = (startValue + endValue) / 2;

    $playlistOrder.setKey(itemId, newOrder);
  }

  return {
    $playlist,
    $currentPlaylistItemId,
    $currentPlaylistItem,
    $currentVersionId,
    $firstFrameOffset,
    $frameDuration,
    $readTimecodeOffsetFromSource,
    setPlaylist,
    addPlaylistItem,
    removeFromPlaylist,
    updatePlaylistItem,
    updateCurrentPlaylistItem,
    jumpToNext,
    jumpToPrevious,
    setCurrentPlaylistItemId,
    setCurrentVersionId,
    setCurrentPlaylistIndex,
    createTimecode,
    toggleReadTimecodeOffsetFromSource,
    setPlaylistItemOrder,
  };
}
